import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import {
  MAPPING_URL,
  PMS_MAPPING_TABS,
} from 'lumen/utils/constants/pms-mapping-constants';
import PmsMappingValueBenchmark from 'lumen/utils/datamodels/mapping/pms-mapping-value-benchmark';

export default class HotelSettingsMappingBenchmarkController extends Controller {
  queryParams = ['activeTab'];

  @service access;
  @service ajax;
  @service alerting;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service toast;

  @tracked activeTab = PMS_MAPPING_TABS.MARKET_CODE;
  @tracked isEditing = false;

  leaveWithoutSaving = false;

  patchMappingTask = task({ enqueue: true }, async (pmsMappingValues) => {
    const queryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id,
      breakdown: this.activeTab,
    };
    const mappingUrl = formatUrl(
      '/api/v3/benchmarkinsight/mapping/',
      queryParams,
      this.ajax.host,
    );
    try {
      await this.ajax.request(mappingUrl, {
        method: 'PATCH',
        contentType: 'application/json',
        data: JSON.stringify({
          mapping: pmsMappingValues,
        }),
      });
      this.showSuccessToast();
    } catch {
      this.alerting.showErrorAlert();
    }

    await this.model.mapping.perform(
      this.activeTab,
      PmsMappingValueBenchmark,
      MAPPING_URL.BENCHMARK,
    );
  });

  get formattedMappingProgress() {
    return this.intl.formatNumber(this.mappingProgress, {
      style: 'percent',
    });
  }

  get hasUnsavedChanges() {
    return this.isEditing;
  }

  get isLoading() {
    return this.model.hierarchy.isRunning || this.model.mapping.isRunning;
  }

  get isMappingDisabled() {
    return !this.access.hasBenchmarkInsightMappingEditRights;
  }

  get isRouteLoading() {
    return this.model.mapping.isRunning;
  }

  get mappingProgress() {
    // Round down the value because backend expects at least 0.9 coverage. This should be reflected in the frontend.
    // Round down to 2 decimals by doing * 100 and / 100, because the unformatted value is a float.
    return (
      Math.floor(this.model.mapping.last.value.meta.mappingCoverage * 100) / 100
    );
  }

  get mappingProgressMessage() {
    if (this.mappingProgress === 1) {
      return {
        color: 'success',
        text: this.intl.t('benchmark_insight.pms_mapping.progress_message.3'),
      };
    }

    if (this.mappingProgress >= this.mappingTarget) {
      return {
        color: 'success',
        text: this.intl.t('benchmark_insight.pms_mapping.progress_message.2'),
      };
    }

    return {
      color: 'warning',
      text: this.intl.t('benchmark_insight.pms_mapping.progress_message.1', {
        mappingTargetPercentage: this.mappingTargetPercentage,
      }),
    };
  }

  get mappingTarget() {
    return this.model.mapping.last.value.meta.mappingCoverageThreshold;
  }

  get mappingTargetPercentage() {
    return this.mappingTarget * 100;
  }

  showSuccessToast() {
    this.toast.success(this.intl.t('general.changes_saved.body'), {
      title: this.intl.t('general.changes_saved'),
      timeout: TOAST_TIMEOUT_LONG,
      showIcon: true,
    });
  }

  showUnsavedChangesAlert() {
    return this.alerting.showWarningAlert({
      buttons: {
        cancel: {
          text: this.intl.t('settings.pms_mapping.action_modal.stay_here'),
        },
        confirm: {
          text: this.intl.t(
            'settings.pms_mapping.action_modal.leave_without_saving',
          ),
        },
      },
      dangerMode: true,
      text: '',
      title: this.intl.t('settings.pms_mapping.action_modal.unsaved_changes'),
    });
  }

  @action
  cancelMappingChanges() {
    this.model.mapping.perform(
      this.activeTab,
      PmsMappingValueBenchmark,
      MAPPING_URL.BENCHMARK,
      false,
    );
    this.isEditing = false;
  }

  @action
  async onChangeTab(tabId) {
    this.leaveWithoutSaving = false;

    if (this.hasUnsavedChanges) {
      this.leaveWithoutSaving = await this.showUnsavedChangesAlert();
    }

    if (!this.hasUnsavedChanges || this.leaveWithoutSaving) {
      this.activeTab = tabId;
      this.isEditing = false;
    }
  }

  @action
  startEditing() {
    if (!this.isEditing) {
      this.isEditing = true;
    }
  }

  @action
  stopEditing() {
    if (this.isEditing) {
      this.isEditing = false;
    }
  }
}
